"use client"

import { useMemo, useState } from "react"
import type { DataImport, DataImportStatus } from "@/schema/data-import"
import type { Query } from "@tanstack/react-query"
import { Button, Popover, PopoverContent, PopoverTrigger, ScrollArea } from "@ui/shad"
import { formatDistanceToNow } from "date-fns"
import { CheckCircle2, Import, Loader2 } from "lucide-react"

import { useFetchDataImports } from "@/hooks/api/data-imports"

import { DataImportDialog } from "./create-data-import-dialog"

const VISIBLE_STATUSES: DataImportStatus[] = ["scraping", "pending", "extracting", "ready_for_review"]
const IN_PROGRESS_STATUSES: DataImportStatus[] = ["scraping", "pending", "extracting"]

function getStatusText(status: DataImportStatus) {
  switch (status) {
    case "scraping":
      return "Downloading..."
    case "pending":
      return "Preparing..."
    case "extracting":
      return "Extracting..."
    case "ready_for_review":
      return "Ready for review"
    default:
      return status
  }
}

function getSourceName(sourceType: string) {
  switch (sourceType) {
    case "wealthbox":
      return "Wealthbox"
    case "redtail":
      return "Redtail"
    default:
      return sourceType
  }
}

export function DataImportStatusPopover() {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [selectedImportId, setSelectedImportId] = useState<string | null>(null)

  const { data: dataImports } = useFetchDataImports({
    refetchInterval: (query: Query<DataImport[]>) =>
      query.state.data?.some((di) => IN_PROGRESS_STATUSES.includes(di.status)) ? 5000 : false,
  })

  const visibleImports = useMemo(
    () => (dataImports ?? []).filter((di: DataImport) => VISIBLE_STATUSES.includes(di.status)),
    [dataImports]
  )

  const hasReadyForReview = useMemo(
    () => visibleImports.some((di: DataImport) => di.status === "ready_for_review" && di.wizard_step !== "review"),
    [visibleImports]
  )

  if (visibleImports.length === 0) return null

  return (
    <>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="iconSm" className="relative">
            <Import className="size-4" />
            <span
              className={`absolute top-0 right-0 flex size-4 items-center justify-center rounded-full text-[10px] ${
                hasReadyForReview ? "bg-red-accent text-background" : "bg-tertiary-foreground text-background"
              }`}
            >
              {visibleImports.length}
            </span>
            <span className="sr-only">Active data imports</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" alignOffset={-20} className="w-80 p-0">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold">Data imports</h3>
          </div>
          <ScrollArea className="max-h-60">
            <div className="divide-y">
              {visibleImports.map((di: DataImport) => (
                <button
                  key={di.id}
                  type="button"
                  className="hover:bg-muted flex w-full items-center gap-3 px-4 py-3 text-left transition-colors"
                  onClick={() => {
                    setSelectedImportId(di.id)
                    setIsPopoverOpen(false)
                  }}
                >
                  {IN_PROGRESS_STATUSES.includes(di.status) ? (
                    <Loader2 className="text-muted-foreground size-4 shrink-0 animate-spin" />
                  ) : (
                    <CheckCircle2 className="text-green-accent size-4 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{getSourceName(di.source_type)} import</p>
                    <p className="text-muted-foreground text-xs">
                      {getStatusText(di.status)} &middot;{" "}
                      {formatDistanceToNow(new Date(di.created_at), { addSuffix: true })}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>

      <DataImportDialog
        open={!!selectedImportId}
        onOpenChange={(open) => {
          if (!open) setSelectedImportId(null)
        }}
        dataImportId={selectedImportId}
      />
    </>
  )
}
